import ContentConfig from '../config/ContentConfig';
import Ksdk from './Ksdk';
import checkRequiredFields from '../common/utils/checkRequiredFields';
import configureReportLogging from '../common/configureReportLogging';
import detectForSource from '../common/Detector/detectForSource';
import getPlayTypeFromContentItems from '../api/content/getPlayTypeFromContentItems';
import isValidConfig from '../config/util/isValidConfig';
import logger from '../log';
import reportAgentInformation from '../common/Reporter/reportAgentInformation';
import { ReporterSections } from '../common/Reporter/constants';
import { ContentCallResponse } from '../common/interfaces';
import { ErrorMessages } from '@kollective-frontend/ksdk-api';
import { OptionalKollectiveSources } from '../playback/interfaces';
import { PrimingData } from '../priming/interfaces';
import { SourceType, WarningMessages } from '../common/constants';
import { getSourceTypeForMime } from '../common/utils/sourceTypeUtils';
import {
  dispatchConfigUpdate,
  dispatchError,
  dispatchInitializeReporter,
  dispatchUpdateGeneralEvent,
  dispatchUpdateReportEvent
} from '../events/dispatch';

const { DIFFERENT_CONTENT_HOST } = WarningMessages;
const { BAD_CONFIG, NO_CONFIG } = ErrorMessages;

const log = logger.getLogger(__filename);

const handleContentResponse = (
  response: ContentCallResponse,
  priming: PrimingData,
  plugin: Ksdk
): Promise<void> => {
  const { data } = response;
  const { play } = plugin.state;
  const config = ContentConfig.getInstance();
  if (data.config) {
    const logData = { data };
    config.merge(data.config, { from: 'final', logData });
  } else {
    log.debug('Did not receive config from backend service');
  }
  dispatchConfigUpdate(config);
  dispatchUpdateGeneralEvent({ config: ContentConfig.getCopy() });
  configureReportLogging(true);

  plugin.legacyConfig = data.legacy || {};
  plugin.setSessionToken(data.sessionToken);
  log.info('setting contentSources:', data.items);
  plugin.contentSources = data.items;
  if (!play.playTypeOverridden) {
    play.playType = getPlayTypeFromContentItems(plugin.contentSources || []);
  }
  const [firstItem] = plugin.contentSources;
  play.contentId = firstItem.id;
  play.tenantId = firstItem.tenantId;
  if (!plugin.contentUrl) {
    plugin.contentUrl = firstItem.url;
  }
  if (!plugin.title) {
    plugin.title = firstItem.title;
  }
  if (!play.sourceType) {
    const mime = firstItem.mime || firstItem.data.mime;
    play.sourceType = getSourceTypeForMime(
      mime,
      plugin.contentUrl
    ) as SourceType;
  }

  if (config.ch !== play.contentHost) {
    log.warn(DIFFERENT_CONTENT_HOST, config.ch, play.contentHost);
  }

  if (!isValidConfig(config)) {
    dispatchError(NO_CONFIG);
    log.error(BAD_CONFIG, config);
    throw Error(NO_CONFIG);
  }

  if (config.app) {
    plugin.sdkConfig.setIntegration({ name: config.app });
  }
  plugin.sdkConfig.setReportInterval(config.report.i);

  const msg = [
    checkRequiredFields(plugin, ['contentUrl']),
    checkRequiredFields(play, ['sourceType', 'contentId', 'tenantId'])
  ]
    .filter(a => typeof a !== 'undefined')
    .join('; ');

  if (msg) {
    const reason = ErrorMessages.MISSING_REQUIRED_FIELDS;
    dispatchError({ reason, information: msg });
    return Promise.reject(new Error(msg));
  }

  dispatchInitializeReporter({
    legacy: plugin.legacyConfig,
    items: plugin.contentSources,
    token: plugin.getKollectiveToken(),
    user: plugin.userToken,
    userEmail: plugin.userEmail
  });

  if (priming.locality) {
    priming.locality.mergeNetworkData(priming.networkData);
    dispatchUpdateReportEvent({
      value: priming.locality,
      section: ReporterSections.LOCALITY_DATA
    });
  }
  reportAgentInformation({ data: priming });
  const value = {} as Record<string, unknown>;
  if (config && config.labels) {
    value.labels = config.labels;
  }
  if (config && config.localities) {
    value.localities = config.localities;
  }
  if (value.localities || value.labels) {
    dispatchUpdateReportEvent({ value, section: ReporterSections.General });
  }

  const { contentUrl: url } = plugin;
  const { playType, sourceType, playTypeOverridden } = play;
  const source = { sourceType, url };
  const { allowedModes } = plugin.playerInteraction;
  const agentData = priming;
  const args = {
    source,
    allowedModes,
    playType,
    agentData,
    playTypeOverridden
  };
  return (
    detectForSource(args)
      .then(detection => plugin.startLoad(detection, source))
      // TODO - msft plugins pass a sources list to the agent playback module.
      // Is it necessary?
      .then(() => {
        const sourceInfo = plugin.getSourceInfo();
        return plugin.playbackModule.startDelivery(
          sourceInfo,
          plugin
        ) as OptionalKollectiveSources;
      })
      .then(plugin.playKollective)
      .catch(plugin.fallbackToCDN)
      .then(() => plugin.playerInteraction.addEventListeners())
  );
};

export default handleContentResponse;
